import { Checkbox, DefaultButton, FocusTrapZone, Icon, IconButton, Layer, mergeStyleSets, Overlay, Popup, Stack, Text } from "@fluentui/react";
import { Toast, Toaster, ToastTitle, useToastController } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { Separator } from "@fluentui/react/lib/Separator";
import { TextField } from "@fluentui/react/lib/TextField";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Joyride, { STATUS, Step } from "react-joyride";
import { useSelector } from "react-redux";
import { useSetState } from "../../hooks/useSetState";
import {
    AppType,
    ChatAppResponse,
    ChatSessionMode,
    Feedback,
    FeedbackResponse,
    QuestionDetails,
    saveSharedChatValueApi,
    saveUserDetails,
    ReasoningEffort,
    getReasoningEffortsForModel,
    GPT5_REASONING_EFFORTS
} from "../../api";
import { appInfo, isAdmin, isViewer, userInfo } from "../../authConfig";
import i18next from "../../i18n/i18n";
import { RootState } from "../../Store";
import { selectSuggestionsStage } from "../../Store/selectors";
import { LockIcon } from "../LockIcon/LockIcon";
import type { AllProductSelectionMeta } from "../QuestionInput";
import { ALLNonTabContentRender } from "./ALLNonTabContentRenderArea";
import styles from "./Answer.module.css";
import { AnswerIcon } from "./AnswerIcon";
import { HtmlParsedAnswer, parseAnswerToHtml } from "./AnswerParser";
import { AnswerWithPreCodeDisplay } from "./AnswerWithPreCodeDisplay";
import { getAttachedFileLoadingMeta } from "./AttachmentLoadingMessage";
import { ReasoningSummary } from "./ReasoningSummary";
import { CitationList } from "./CitationList";
import { SelectedProductUnAvailableInfoMsgDialog } from "./SelectedProductUnAvailableInfoMsgDialog";
import "./UnifiedAnswer.css";
import { ContextualMenu, DirectionalHint, IContextualMenuItem } from "@fluentui/react";
import { Stack as MUIStack, Tooltip, IconButton as MuiIconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
interface Props {
    response: any;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, citationIndex: number) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string, appType: AppType) => void;
    onFeedbackSubmitClicked?: (feedback: Feedback, conv_id: string, appType: AppType) => void;
    showFollowupQuestions?: boolean;
    primaryId: string | number;
    onClickLikeDislike: () => void;
    isAdminUser?: boolean;
    doctype?: boolean;
    displayLink?: () => void;
    isLastAnswer: boolean;
    isFeedbackPage: boolean;
    onSend: (string: AppType) => void;
    isLoading: boolean;
    selectedAppType: string;
    setSelectedAppType: (appType: AppType) => void;
    lastAnswerIndexRefValue: number;
    onTabClicked: () => void;
    currentPageView: ChatSessionMode;
    answerAreaRef?: any;
    chatResponseAreaRef?: any;
    lastQuestionIndexValue?: string;
    activateAnswerTour?: boolean;
    setActivateAnswerTour?: (tourFlag: boolean) => void;
    combinedAppTypeResponse?: boolean;
    // When an <attachment>…</attachment> link is clicked inside the answer body
    onAttachmentLinkClick?: (filename: string, page?: number) => void;
    // Files attached for the corresponding user question
    questionFilesAttached?: QuestionDetails["filesAttached"];
    // AnswersHistory specific props
    isHistory: boolean;
    updateStreamedAnswer: (index: number, updatedAnswer: ChatAppResponse, appType: AppType) => void;
    onRenerateClick?: (option: ReasoningEffort, convId: string) => void;
    onChangeProductScenarioAndRegenerate?: () => void;
    currentAnswerIndex: number;
    isRegenerateActive: boolean;
    onAnswerVersionChange: (currentVersion: number) => void;
    answerVersions: number;
    conversationDate?: string | null;
}

interface LikeComponentProps {
    isLikePopupVisible: boolean;
    appType?: string;
    isHistory?: boolean | undefined;
    feedbackText?: string | undefined;
    hideLikePopup: () => void;
}

interface DisLikeComponentProps {
    isDisLikePopupVisible: boolean;
    appType?: string;
    isHistory?: boolean | undefined;
    feedbackText?: string | undefined;
    hideDisLikePopup: () => void;
    selectedOptions?: Array<number>;
}

interface ShareProps {
    isSharChatPopupVisible: boolean;
}

interface State {
    run: boolean;
    steps: Step[];
}

const popupStyles = mergeStyleSets({
    root: {
        background: "rgba(0, 0, 0, 0.5)",
        bottom: "0",
        left: "0",
        position: "absolute",
        right: "0",
        top: "0"
    },
    content: {
        background: "white",
        left: "50%",
        maxWidth: "1000px",
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: "10px",
        border: "2px solid",
        borderColor: "grey",
        padding: "15px"
    }
});

const stackTokens = { childrenGap: 10, padding: 25 };

const UNIFIED_TOASTER_ID = "unified-answer-toaster";

interface RegenerateDropdownProps {
    visible: boolean;
    target: HTMLElement | null;
    onDismiss: () => void;
    reasoningEffortSelected: string | null;
    onRegenerateOptionSelected: (effort: ReasoningEffort) => void;
}

export const UnifiedAnswer = ({
    response,
    isSelected,
    isStreaming,
    primaryId,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    onFeedbackSubmitClicked,
    showFollowupQuestions,
    onClickLikeDislike,
    doctype,
    isAdminUser,
    displayLink,
    isLastAnswer,
    isFeedbackPage,
    onSend,
    isLoading,
    selectedAppType,
    setSelectedAppType,
    lastAnswerIndexRefValue,
    onTabClicked,
    currentPageView,
    answerAreaRef,
    chatResponseAreaRef,
    lastQuestionIndexValue,
    activateAnswerTour,
    setActivateAnswerTour,
    combinedAppTypeResponse,
    onAttachmentLinkClick,
    questionFilesAttached,
    isHistory,
    updateStreamedAnswer,
    onRenerateClick,
    onChangeProductScenarioAndRegenerate,
    currentAnswerIndex,
    isRegenerateActive,
    onAnswerVersionChange,
    answerVersions,
    conversationDate
}: Props) => {
    const answer: ChatAppResponse = response["FAQ"];
    const manualsAnswer: ChatAppResponse = response["Manuals"];
    const allAnswer: ChatAppResponse = response["ALL"];
    const totalVersions = answerVersions !== null ? answerVersions : 1;
    const attachedFileLoadingMeta = useMemo(() => getAttachedFileLoadingMeta(questionFilesAttached), [questionFilesAttached]);
    const animatedMessageCharacters = useMemo(() => Array.from(attachedFileLoadingMeta.message), [attachedFileLoadingMeta.message]);

    const renderLoadingSkeleton = () => (
        <Box sx={{ width: "100%" }}>
            {attachedFileLoadingMeta.message && (
                <Text
                    style={{ color: "#00000099", marginBottom: "4px" }}
                    title={attachedFileLoadingMeta.fileNames.length > 0 ? attachedFileLoadingMeta.fileNames.join(", ") : undefined}
                >
                    <span className={styles.loadingLabelText}>
                        {animatedMessageCharacters.map((character, index) => (
                            <span key={`loading-letter-${index}`} className={styles.loadingLabelLetter} style={{ animationDelay: `${index * 0.035}s` }}>
                                {character === " " ? "\u00A0" : character}
                            </span>
                        ))}
                    </span>
                </Text>
            )}
            <Skeleton animation="wave" sx={{ width: "100%" }} />
            <Skeleton animation="wave" sx={{ width: "90%" }} />
            <Skeleton animation="wave" sx={{ width: "80%" }} />
            <Skeleton animation="wave" sx={{ width: "70%" }} />
            <Skeleton animation="wave" sx={{ width: "60%" }} />
            <Skeleton animation="wave" sx={{ width: "50%" }} />
        </Box>
    );

    // FAQ answer processing
    const faqFollowupQuestions = answer?.context?.followup_questions;
    const faqDataPoints = answer?.context?.data_points ?? [];
    const faqMessageContent = typeof answer?.output_text === "string" ? answer?.output_text : "";
    const faqFileMapping = answer?.context?.file_mapping;
    const { dispatchToast } = useToastController(UNIFIED_TOASTER_ID);
    const overAllCurrentSessionDataPoints = isFeedbackPage
        ? useSelector((store: RootState) => store.feedBackPage.overAllCurrentSessionDataPoints)
        : useSelector((store: RootState) => store.chatComponent.overAllCurrentSessionDataPoints);
    const isSuggestionsActive = useSelector(selectSuggestionsStage) !== "idle";

    const faqParsedAnswer = useMemo(
        () => parseAnswerToHtml(faqMessageContent, isStreaming, onCitationClicked, faqDataPoints, faqFileMapping, "FAQ", isFeedbackPage ? "Feedback" : "Chat"),
        [answer, overAllCurrentSessionDataPoints.FAQ]
    );

    // Manual answer processing
    const manualsFollowupQuestions = manualsAnswer?.context?.followup_questions;
    const manualsDataPoints = manualsAnswer?.context?.data_points ?? [];
    const manualsMessageContent = typeof manualsAnswer?.output_text === "string" ? manualsAnswer?.output_text : "";
    const manualsFileMapping = manualsAnswer?.context?.file_mapping;
    const manualParsedAnswer = useMemo(
        () =>
            parseAnswerToHtml(
                manualsMessageContent,
                isStreaming,
                onCitationClicked,
                manualsDataPoints,
                manualsFileMapping,
                "Manuals",
                isFeedbackPage ? "Feedback" : "Chat"
            ),
        [manualsAnswer, overAllCurrentSessionDataPoints.Manuals]
    );

    // All answer processing
    const allFollowupQuestions = allAnswer?.context?.followup_questions;
    const allDataPoints = allAnswer?.context?.data_points ?? [];
    const allMessageContent = typeof allAnswer?.output_text === "string" ? allAnswer?.output_text : "";
    const allFileMapping = allAnswer?.context?.file_mapping;
    const allParsedAnswer = useMemo(
        () => parseAnswerToHtml(allMessageContent, isStreaming, onCitationClicked, allDataPoints, allFileMapping, "ALL", isFeedbackPage ? "Feedback" : "Chat"),
        [allAnswer, overAllCurrentSessionDataPoints.ALL]
    );

    // State management
    const [tabIndex, setTabIndex] = useState(selectedAppType + primaryId);
    const [prevSelectedAppType, setPrevSelectedAppType] = useState(selectedAppType);

    // Tour related states
    const [tourRunning, setTourRunning] = useState(false);
    const [tourStepIndex, setTourStepIndex] = useState(0);

    // Other states from original components
    const [isLikePopupVisible, { setTrue: showLikePopup, setFalse: hideLikePopup }] = useBoolean(false);
    const [isDisLikePopupVisible, { setTrue: showDisLikePopup, setFalse: hideDisLikePopup }] = useBoolean(false);
    const [isSharChatPopupVisible, { setTrue: showShareChatPopup, setFalse: hideShareChatPopup }] = useBoolean(false);
    const [isSelectedProductUnAvailablePopupOpen, setIsSelectedProductUnAvailablePopupOpen] = useState(false);
    const [productUnAvailableWarningMsg, setProductUnAvailableWarningMsg] = useState<string | undefined>(undefined);

    const currentTabAnswer = selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer;
    const currentParsedAnswer = selectedAppType === "ALL" ? allParsedAnswer : selectedAppType === "FAQ" ? faqParsedAnswer : manualParsedAnswer;
    const currentConversationDate = currentTabAnswer
        ? (currentTabAnswer.conversation_date ?? currentTabAnswer.context?.conversationDate ?? conversationDate)
        : null;
    const currentSelectionMeta = useMemo<AllProductSelectionMeta | null>(() => {
        const currentTabMeta = (currentTabAnswer as ChatAppResponse & { allProductSelectionMeta?: AllProductSelectionMeta | null })?.allProductSelectionMeta;
        return currentTabMeta ?? null;
    }, [currentTabAnswer]);
    const autoSelectionSnapshot = currentSelectionMeta?.source === "auto" ? (currentSelectionMeta.autoSelectionSnapshot ?? null) : null;
    const [showAutoSelectionSnapshot, setShowAutoSelectionSnapshot] = useState(false);

    // Selected product logic (same source as the AnswerIcon tooltip: current tab only)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let selectedProduct: any = currentTabAnswer?.context?.product_filter;

    const selectedLocale = sessionStorage?.getItem("selectedLocale") ?? "ja";
    selectedProduct = appInfo?.product_categories[selectedLocale]?.find(
        (item: any) =>
            (selectedProduct?.manual_key && item.manual_key === selectedProduct.manual_key) ||
            (selectedProduct?.faq_key && item.faq_key === selectedProduct.faq_key)
    );
    const currentAnswerContext = currentTabAnswer?.context;
    const generatedForProductText = useMemo(() => {
        if (!selectedProduct?.text) {
            return "";
        }

        // When the whole-product filter is ALL, show just "ALL" without appending version/sub segments.
        // Two representations exist:
        //   - New answers: { manual_key: "0", faq_key: "0" }         (both explicitly "0")
        //   - History answers: { manual_key: "", faq_key: "0" } or { manual_key: "0", faq_key: "" }
        // In all cases both keys are either "0" or empty — no real product key is present.
        const productFilter = currentAnswerContext?.product_filter;
        const manualKey = String(productFilter?.manual_key ?? "");
        const faqKey = String(productFilter?.faq_key ?? "");
        const isAllProduct = (manualKey === "0" || manualKey === "") && (faqKey === "0" || faqKey === "");
        if (isAllProduct) {
            return selectedProduct.text;
        }

        const productParts = [selectedProduct.text];
        const currentProductVersion = currentAnswerContext?.productVersion;
        const currentSubProduct = currentAnswerContext?.sub_product_filter;
        const isSubOnlyProduct = String(selectedProduct?.is_sub_only ?? "") === "true";

        if (isSubOnlyProduct) {
            if (currentSubProduct?.manual_sub_key === "0" || currentSubProduct?.faq_sub_key === "0") {
                productParts.push("ALL");
            } else if (currentSubProduct) {
                const selectedSubOnlyEntry = selectedProduct?.versions?.find(
                    (entry: any) =>
                        (entry?.manual_sub_key && entry.manual_sub_key === currentSubProduct.manual_sub_key) ||
                        (entry?.faq_sub_key && entry.faq_sub_key === currentSubProduct.faq_sub_key)
                );
                if (selectedSubOnlyEntry?.text) {
                    productParts.push(selectedSubOnlyEntry.text);
                }
            }
            return productParts.join(" / ");
        }

        const selectedVersionEntry = selectedProduct?.versions?.find(
            (entry: any) =>
                (entry?.manual_version_key && entry.manual_version_key === currentProductVersion?.manual_version_key) ||
                (entry?.faq_version_key && entry.faq_version_key === currentProductVersion?.faq_version_key)
        );

        if (currentProductVersion?.manual_version_key === "0" || currentProductVersion?.faq_version_key === "0") {
            productParts.push("ALL");
        } else if (selectedVersionEntry?.text) {
            productParts.push(selectedVersionEntry.text);
        }

        if (currentSubProduct?.manual_sub_key === "0" || currentSubProduct?.faq_sub_key === "0") {
            productParts.push("ALL");
        } else if (selectedVersionEntry?.sub_versions?.length) {
            const selectedSubVersionEntry = selectedVersionEntry.sub_versions.find(
                (entry: any) =>
                    (entry?.manual_sub_key && entry.manual_sub_key === currentSubProduct?.manual_sub_key) ||
                    (entry?.faq_sub_key && entry.faq_sub_key === currentSubProduct?.faq_sub_key)
            );
            if (selectedSubVersionEntry?.text) {
                productParts.push(selectedSubVersionEntry.text);
            }
        }

        return productParts.filter(Boolean).join(" / ");
    }, [currentAnswerContext?.product_filter, currentAnswerContext?.productVersion, currentAnswerContext?.sub_product_filter, selectedProduct]);
    const showGeneratedForProductContext = Boolean(generatedForProductText) && Boolean(currentParsedAnswer?.answerHtml);
    const autoSelectionSelectedConfidenceText =
        autoSelectionSnapshot?.selectedConfidence === null || autoSelectionSnapshot?.selectedConfidence === undefined
            ? "-"
            : `${autoSelectionSnapshot.selectedConfidence}%`;
    const autoSelectionSecondConfidenceText =
        autoSelectionSnapshot?.secondConfidence === null || autoSelectionSnapshot?.secondConfidence === undefined
            ? "-"
            : `${autoSelectionSnapshot.secondConfidence}%`;
    const autoSelectionMarginText =
        autoSelectionSnapshot?.confidenceMargin === null || autoSelectionSnapshot?.confidenceMargin === undefined
            ? "-"
            : `${Math.round(autoSelectionSnapshot.confidenceMargin)}%`;
    const autoSelectionSelectedRerankerScoreText =
        autoSelectionSnapshot?.selectedAverageRerankerScore === null || autoSelectionSnapshot?.selectedAverageRerankerScore === undefined
            ? "-"
            : Number(autoSelectionSnapshot.selectedAverageRerankerScore).toFixed(2);
    const autoSelectionSecondRerankerScoreText =
        autoSelectionSnapshot?.secondAverageRerankerScore === null || autoSelectionSnapshot?.secondAverageRerankerScore === undefined
            ? "-"
            : Number(autoSelectionSnapshot.secondAverageRerankerScore).toFixed(2);
    const autoSelectionSelectedRerankerQualifiedCountText =
        autoSelectionSnapshot?.selectedRerankerQualifiedCount === null || autoSelectionSnapshot?.selectedRerankerQualifiedCount === undefined
            ? "-"
            : String(Math.max(0, Number(autoSelectionSnapshot.selectedRerankerQualifiedCount)));
    const autoSelectionSecondRerankerQualifiedCountText =
        autoSelectionSnapshot?.secondRerankerQualifiedCount === null || autoSelectionSnapshot?.secondRerankerQualifiedCount === undefined
            ? "-"
            : String(Math.max(0, Number(autoSelectionSnapshot.secondRerankerQualifiedCount)));
    const autoSelectionRerankerGapText =
        autoSelectionSnapshot?.rerankerGap === null || autoSelectionSnapshot?.rerankerGap === undefined
            ? "-"
            : Number(autoSelectionSnapshot.rerankerGap).toFixed(2);
    const autoSelectionSecondProductText = autoSelectionSnapshot?.secondProductLabel ?? i18next.t("answer.autoSelectionSnapshotNoSecond");
    const isServerModelSelection = autoSelectionSnapshot?.decisionSource === "server_model";
    const autoSelectionDecisionSourceText =
        autoSelectionSnapshot?.decisionSource === "server_model"
            ? i18next.t("answer.autoSelectionSnapshotDecisionSourceServerModel")
            : autoSelectionSnapshot?.decisionSource === "ui_fallback"
              ? i18next.t("answer.autoSelectionSnapshotDecisionSourceUIFallback")
              : null;
    const autoSelectionDecisionReason = autoSelectionSnapshot?.decisionReason ?? null;
    const isRerankerSelection = autoSelectionSnapshot?.selectionMethod === "reranker";
    const autoSelectionMethodText = isRerankerSelection
        ? i18next.t("answer.autoSelectionSnapshotSelectionMethodReranker")
        : autoSelectionSnapshot?.selectionMethod === "confidence"
          ? i18next.t("answer.autoSelectionSnapshotSelectionMethodConfidence")
          : autoSelectionSnapshot?.decisionSource === "server_model"
            ? i18next.t("answer.autoSelectionSnapshotSelectionMethodServerModel")
            : i18next.t("answer.autoSelectionSnapshotSelectionMethodUnknown");
    const autoSelectionPrimaryMetricLabel = isRerankerSelection
        ? i18next.t("supportingContent.rerankerScore")
        : i18next.t("answer.autoSelectionSnapshotSelectedConfidenceLabel");
    const autoSelectionPrimaryMetricValue = isRerankerSelection
        ? `${autoSelectionSelectedRerankerScoreText} (${autoSelectionSelectedRerankerQualifiedCountText} ${i18next.t(
              "answer.autoSelectionSnapshotSelectedHitsLabel"
          )})`
        : `${autoSelectionSelectedConfidenceText} (${autoSelectionSnapshot?.selectedHits ?? 0} ${i18next.t("answer.autoSelectionSnapshotSelectedHitsLabel")})`;
    const autoSelectionSecondMetricLabel = isRerankerSelection
        ? i18next.t("supportingContent.rerankerScore")
        : i18next.t("answer.autoSelectionSnapshotSecondConfidenceLabel");
    const autoSelectionSecondMetricValue = isRerankerSelection
        ? `${autoSelectionSecondRerankerScoreText} (${autoSelectionSecondRerankerQualifiedCountText} ${i18next.t(
              "answer.autoSelectionSnapshotSelectedHitsLabel"
          )})`
        : autoSelectionSecondConfidenceText;
    const autoSelectionLeadLabel = isRerankerSelection
        ? i18next.t("answer.autoSelectionSnapshotLeadLabel")
        : i18next.t("answer.autoSelectionSnapshotLeadLabel");
    const autoSelectionLeadValue = isRerankerSelection
        ? autoSelectionRerankerGapText
        : `${autoSelectionMarginText} (${i18next.t("answer.autoSelectionSnapshotLeadRequiredLabel", {
              value: Math.round(autoSelectionSnapshot?.minMargin ?? 0)
          })})`;
    // Admin-only (feedback page) PTF customer-config audit fields.
    const formatScoreAndCitations = (score: number | null | undefined, citationCount: number | null | undefined): string =>
        `${score === null || score === undefined ? "-" : score} (${citationCount ?? 0} ${i18next.t("answer.autoSelectionSnapshotSelectedHitsLabel")})`;
    const modelSelectedScoreText = formatScoreAndCitations(autoSelectionSnapshot?.modelSelectedScore, autoSelectionSnapshot?.modelSelectedCitationCount);
    const ptfSelectedScoreText = formatScoreAndCitations(autoSelectionSnapshot?.ptfSelectedScore, autoSelectionSnapshot?.ptfSelectedCitationCount);
    const customerConfigComparison = autoSelectionSnapshot?.customerConfigComparison ?? null;
    const customerConfigComparisonText =
        customerConfigComparison === "config_lower"
            ? i18next.t("answer.autoSelectionSnapshotConfigVersionLower", {
                  configVersion: autoSelectionSnapshot?.customerConfigVersionMajor ?? "-",
                  effectiveVersion: autoSelectionSnapshot?.customerConfigEffectiveVersionMajor ?? "-"
              })
            : customerConfigComparison === "config_higher"
              ? i18next.t("answer.autoSelectionSnapshotConfigVersionHigher", {
                    configVersion: autoSelectionSnapshot?.customerConfigVersionMajor ?? "-",
                    effectiveVersion: autoSelectionSnapshot?.customerConfigEffectiveVersionMajor ?? "-"
                })
              : null;
    const customerConfigStatusKeys: Record<string, string> = {
        not_registered: "answer.autoSelectionSnapshotConfigStatusNotRegistered",
        registered_no_version: "answer.autoSelectionSnapshotConfigStatusRegisteredNoVersion",
        lookup_skipped: "answer.autoSelectionSnapshotConfigStatusLookupSkipped",
        lookup_failed: "answer.autoSelectionSnapshotConfigStatusLookupFailed"
    };
    const customerConfigStatusText =
        customerConfigComparison && customerConfigStatusKeys[customerConfigComparison] ? i18next.t(customerConfigStatusKeys[customerConfigComparison]) : null;

    // Additional state management
    const isActiveAnswer = lastAnswerIndexRefValue === Number(primaryId);
    const isCurrentAnswerGenerating = isActiveAnswer && (isStreaming || isLoading || isRegenerateActive);
    const hidePerRowFeedback =
        ((isStreaming || isLoading) && isActiveAnswer) ||
        (selectedAppType === "ALL" && allParsedAnswer.answerHtml === "") ||
        (selectedAppType === "FAQ" && faqParsedAnswer.answerHtml === "") ||
        (selectedAppType === "Manuals" && manualParsedAnswer.answerHtml === "");
    const [likeSubmittedForAppType, setLikeSubmittedForAppType] = useState<boolean>(false);
    const [disLikeSubmittedForAppType, setDisLikeSubmittedForAppType] = useState<boolean>(false);

    // Feedbacks states and its Respective Conversation ID's
    const conv_id_faq = answer?.context?.conv_id ?? "";
    const conv_id_manuals = manualsAnswer?.context?.conv_id ?? "";
    const conv_id_all = allAnswer?.context?.conv_id ?? "";
    const [feedBackInput, setFeedbackInput] = useState<FeedbackResponse>({});

    // Check if user is WAP or Customer and display TAB according to setting
    const isWap = String(userInfo?.isWap).toLowerCase() === "true";
    const useAll = appInfo?.use_all_tab === true;
    const showAllTab = isWap && useAll;
    const showAllOnly = !isWap && useAll;

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [showRegenerateMenu, setShowRegenerateMenu] = useState<boolean>(false);
    const [regenerateTarget, setRegenerateTarget] = useState<HTMLElement | null>(null);
    const reasoningEffort = (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.reasoning_effort ?? null;
    const chatModel = (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.chat_model;
    const currentReasoningSummary = (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.reasoning_summary;
    useEffect(() => {
        setShowAutoSelectionSnapshot(false);
    }, [primaryId, selectedAppType, autoSelectionSnapshot?.selectedProductLabel, autoSelectionSnapshot?.selectedConfidence]);
    // Effect for handling sanitized HTML content
    useEffect(() => {
        let feedbacks: FeedbackResponse = {};
        if (selectedAppType === "ALL") {
            feedbacks = allAnswer?.context?.feedback ?? { status: false, data: { text: "", type: [], time: "" } };
        }

        if (selectedAppType === "FAQ") {
            feedbacks = answer?.context?.feedback ?? { status: false, data: { text: "", type: [], time: "" } };
        }

        if (selectedAppType === "Manuals") {
            feedbacks = manualsAnswer?.context?.feedback ?? { status: false, data: { text: "", type: [], time: "" } };
        }
        setFeedbackInput(feedbacks);
        const feedbackCategory = Number(feedbacks["category"]);
        if (feedbackCategory === 2) {
            displayLink ? displayLink() : null;
        }
        setLikeSubmittedForAppType(feedbackCategory === 1);
        setDisLikeSubmittedForAppType(feedbackCategory === 2);
    }, [selectedAppType, allParsedAnswer, faqParsedAnswer, manualParsedAnswer]);

    // Effect for tab management
    useEffect(() => {
        if (selectedAppType !== prevSelectedAppType) {
            setTabIndex(selectedAppType + primaryId);
            setPrevSelectedAppType(selectedAppType);
        }
    }, [selectedAppType, primaryId, prevSelectedAppType]);

    const parseQueryParams = (queryvalue: string) => {
        const queryparameter = new URLSearchParams(queryvalue);
        const params: { [key: string]: string } = {};
        for (const [key, value] of queryparameter.entries()) {
            params[key] = value;
        }
        return params;
    };

    const notify = (message: string) =>
        dispatchToast(
            <Toast style={{ backgroundColor: "white", borderRadius: "10px", color: "green", width: "300px" }}>
                <ToastTitle style={{ color: "black", fontWeight: "bold" }}>{message}</ToastTitle>
            </Toast>,
            { intent: "success" }
        );

    // Tour steps definition
    const [tourOne, setTourOne] = useSetState<State>({
        run: false,
        steps: [
            {
                content: i18next.t(`appTour.answerTour.shareIcon`),
                target: `#ShareIcon${lastQuestionIndexValue}`,
                disableBeacon: true,
                floaterProps: {
                    disableAnimation: true,
                    autoOpen: true
                }
            },
            {
                content: i18next.t(`appTour.answerTour.likeIcon`),
                target: `#LikeIcon${lastQuestionIndexValue}`
            },
            {
                content: i18next.t(`appTour.answerTour.disLikeIcon`),
                target: `#DisLikeIcon${lastQuestionIndexValue}`
            }
        ]
    });

    const [tourTwo, setTourTwo] = useSetState<State>({
        run: false,
        steps: [
            {
                content: i18next.t(`appTour.answerTour.ALLTabInfo`),
                target: `[data-tour-id="ALLTab${lastQuestionIndexValue}"]`,
                disableBeacon: true,
                floaterProps: {
                    disableAnimation: true,
                    autoOpen: true
                },
                styles: {
                    tooltip: {
                        width: "200px",
                        maxWidth: "200px"
                    }
                }
            },
            {
                content: i18next.t(`appTour.answerTour.faqTabInfo`),
                target: `[data-tour-id="FAQTab${lastQuestionIndexValue}"]`,
                disableBeacon: true,
                floaterProps: {
                    disableAnimation: true,
                    autoOpen: true
                },
                styles: {
                    tooltip: {
                        width: "200px",
                        maxWidth: "200px"
                    }
                }
            },
            {
                content: i18next.t(`appTour.answerTour.manualsTabInfo`),
                target: `[data-tour-id="ManualsTab${lastQuestionIndexValue}"]`
            }
        ]
    });

    const endTourOne = useCallback(() => {
        setTourOne(prevState => ({
            ...prevState,
            run: false
        }));
        if (userInfo.answerTourDone === "false") {
            saveUserSettings({ chatTourDone: "true", templateTourDone: "true", answerTourDone: "true", answerTabTourDone: "false" });
        }
        userInfo.answerTourDone = "true";
        setTimeout(() => {
            setTourTwo(prevState => ({
                ...prevState,
                run: true
            }));
        }, 1500);
    }, []);

    const endTourTwo = useCallback(() => {
        setTourTwo(prevState => ({
            ...prevState,
            run: false
        }));
        if (userInfo.answerTabTourDone === "false") {
            saveUserSettings({ chatTourDone: "true", templateTourDone: "true", answerTourDone: "true", answerTabTourDone: "true" });
        }
        userInfo.answerTabTourDone = "true";
    }, []);

    // Tour callback handler
    const handleTourCallback = (data: any, isTabTour: boolean) => {
        const { status } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (isTabTour) {
                endTourTwo();
                if (setActivateAnswerTour) {
                    setActivateAnswerTour(false);
                }
            } else {
                endTourOne();
            }
        } else {
            // status in running -> means tour is active.
            if (answerAreaRef?.current && status === STATUS.RUNNING) {
                setTimeout(() => {
                    adjustScroll(isTabTour);
                }, 1000);
            }
        }
    };

    const adjustScroll = (isTabTour: boolean) => {
        if (answerAreaRef?.current && chatResponseAreaRef?.current) {
            answerAreaRef.current.scrollTop = isTabTour
                ? chatResponseAreaRef.current.offsetTop - answerAreaRef.current.offsetTop - 50
                : answerAreaRef.current.scrollHeight;
        }
    };

    // Start tour effect
    useEffect(() => {
        if (activateAnswerTour || isLastAnswer) {
            // need to update the tour guide to point to last question.
            setTourOne(prevState => ({
                ...prevState,
                run: false,
                steps: [
                    {
                        content: i18next.t(`appTour.answerTour.shareIcon`),
                        target: `#ShareIcon${lastQuestionIndexValue}`,
                        disableBeacon: true,
                        floaterProps: {
                            disableAnimation: true,
                            autoOpen: true
                        }
                    },
                    {
                        content: i18next.t(`appTour.answerTour.likeIcon`),
                        target: `#LikeIcon${lastQuestionIndexValue}`
                    },
                    {
                        content: i18next.t(`appTour.answerTour.disLikeIcon`),
                        target: `#DisLikeIcon${lastQuestionIndexValue}`
                    }
                ]
            }));
            setTourTwo(prevState => ({
                ...prevState,
                run: false,
                steps: [
                    {
                        content: i18next.t(`appTour.answerTour.ALLTabInfo`),
                        target: `[data-tour-id="ALLTab${lastQuestionIndexValue}"]`,
                        disableBeacon: true,
                        floaterProps: {
                            disableAnimation: true,
                            autoOpen: true
                        },
                        styles: {
                            tooltip: {
                                width: "200px",
                                maxWidth: "200px"
                            }
                        }
                    },
                    {
                        content: i18next.t(`appTour.answerTour.faqTabInfo`),
                        target: `[data-tour-id="FAQTab${lastQuestionIndexValue}"]`,
                        disableBeacon: true,
                        floaterProps: {
                            disableAnimation: true,
                            autoOpen: true
                        },
                        styles: {
                            tooltip: {
                                width: "200px",
                                maxWidth: "200px"
                            }
                        }
                    },
                    {
                        content: i18next.t(`appTour.answerTour.manualsTabInfo`),
                        target: `[data-tour-id="ManualsTab${lastQuestionIndexValue}"]`
                    }
                ]
            }));
        }

        if (isStreaming === false && activateAnswerTour) {
            // scroll to last answer
            if (userInfo.answerTourDone === "false" && userInfo.answerTabTourDone === "false") {
                adjustScroll(false);
                setTimeout(() => {
                    setTourOne(prevState => ({
                        ...prevState,
                        run: true
                    }));
                }, 2500);
            } else if (userInfo.answerTourDone === "true" && userInfo.answerTabTourDone === "false") {
                adjustScroll(true);
                setTimeout(() => {
                    setTourTwo(prevState => ({
                        ...prevState,
                        run: true
                    }));
                }, 2500);
            } else {
                // tour was activate using btn click when all the tours are done.
                adjustScroll(false);
                setTimeout(() => {
                    setTourOne(prevState => ({
                        ...prevState,
                        run: true
                    }));
                }, 2500);
            }
        }

        if (isStreaming === false && !activateAnswerTour) {
            // when Answer private dlg is active don't activate the app tour.
            const isAnswerPrivateDlgActive = localStorage.getItem("private_warning") === "true" && localStorage.getItem("show_warning") === "true";
            if (!isAnswerPrivateDlgActive) {
                if (userInfo.answerTourDone === "false" && userInfo.answerTabTourDone === "false") {
                    adjustScroll(false);
                    setTimeout(() => {
                        setTourOne(prevState => ({
                            ...prevState,
                            run: true
                        }));
                    }, 2500);
                } else if (userInfo.answerTourDone === "true" && userInfo.answerTabTourDone === "false") {
                    adjustScroll(true);
                    setTimeout(() => {
                        setTourTwo(prevState => ({
                            ...prevState,
                            run: true
                        }));
                    }, 2500);
                }
            }
        }
    }, [lastQuestionIndexValue, isStreaming, activateAnswerTour, isLastAnswer]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowRegenerateMenu(false);
            }
        };

        if (showRegenerateMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showRegenerateMenu]);

    const saveUserSettings = async (settings: object) => {
        try {
            await saveUserDetails(settings)
                .then(response => {
                    return response.json();
                })
                .catch(error => {
                    console.error("Error saving user data:", error);
                });
        } catch (error) {
            console.error("Error saving user data:", error);
        }
    };

    // Tab change handler

    const appTypeFromTabIndex = (idx: string): AppType => (idx.includes("ALL") ? "ALL" : idx.includes("FAQ") ? "FAQ" : "Manuals");

    const answerFromTabIndex = (idx: string): any => (idx.includes("ALL") ? allAnswer : idx.includes("FAQ") ? answer : manualsAnswer);

    const handleChange = (event: React.SyntheticEvent, newtabIndex: string) => {
        if (!String(window.location.pathname).includes("/assistant/feedbacks") && (isLoading || isStreaming)) {
            // not feedback page & page is loading or streaming.
            event.preventDefault();
        } else {
            const tabString = appTypeFromTabIndex(newtabIndex);
            if (!String(window.location.pathname).includes("/assistant/feedbacks")) {
                // not feedback page then check conditions below
                if (currentPageView === "chat") {
                    // for chat view or new chat -> selected product contains both the faqkey value & manual key value on streaming answers or answers object.
                    if ((tabString === "FAQ" && selectedProduct?.faq_key === "") || (tabString === "Manuals" && selectedProduct?.manual_key === "")) {
                        setIsSelectedProductUnAvailablePopupOpen(true);
                        setProductUnAvailableWarningMsg(
                            tabString === "FAQ"
                                ? i18next.t("answer.faqUnavailableForSelectedProduct")
                                : i18next.t("answer.manualsUnavailableForSelectedProduct")
                        );
                        return;
                    }
                    setTabIndex(newtabIndex);
                } else {
                    // page view is history.
                    // the answers object have only only on key either faq key or manual key for existing answers.
                    if (tabString !== "ALL") {
                        const flag = validateProductKeyAvailableForHistoryView(tabString, newtabIndex);
                        if (!flag) {
                            return;
                        }
                    }
                }
                if (!answerFromTabIndex(newtabIndex)) {
                    onSend(tabString);
                }
            } else {
                // feedback page means simply switch the tab.
                setTabIndex(newtabIndex);
            }
            setSelectedAppType(tabString);
            onTabClicked();
        }
    };

    const validateProductKeyAvailableForHistoryView = (appTypeValue: AppType, newtabIndex: string) => {
        if ((appTypeValue === "FAQ" && faqParsedAnswer.answerHtml !== "") || (appTypeValue === "Manuals" && manualParsedAnswer.answerHtml !== "")) {
            setTabIndex(newtabIndex);
            return true;
        } else {
            // if any answer is empty.
            let targetKeyValue: string | undefined = "";
            let targetKeyType: string | undefined = "";
            if (appTypeValue === "FAQ" && faqParsedAnswer.answerHtml === "") {
                targetKeyValue = selectedProduct?.manual_key;
                targetKeyType = "Manuals";
            }

            if (appTypeValue === "Manuals" && manualParsedAnswer.answerHtml === "") {
                targetKeyValue = selectedProduct?.faq_key;
                targetKeyType = "FAQ";
            }

            const selectedLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
            const filteredSelectedProduct = appInfo.product_categories[selectedLocale]?.filter((item: any) =>
                targetKeyType === "FAQ" ? item.faq_key === targetKeyValue : item.manual_key === targetKeyValue
            )[0];
            if (
                (appTypeValue === "FAQ" && filteredSelectedProduct?.faq_key === "") ||
                (appTypeValue === "Manuals" && filteredSelectedProduct?.manual_key === "")
            ) {
                setIsSelectedProductUnAvailablePopupOpen(true);
                setProductUnAvailableWarningMsg(
                    appTypeValue === "FAQ" ? i18next.t("answer.faqUnavailableForSelectedProduct") : i18next.t("answer.manualsUnavailableForSelectedProduct")
                );
                return false;
            } else {
                setIsSelectedProductUnAvailablePopupOpen(false);
                setProductUnAvailableWarningMsg("");
                setTabIndex(newtabIndex);
                return true;
            }
        }
    };

    // Feedback handlers
    const handleLikeClicked = () => {
        if (onClickLikeDislike) {
            onClickLikeDislike();
        }
        showLikePopup();
    };

    const handleDislikeClicked = () => {
        if (onClickLikeDislike) {
            onClickLikeDislike();
        }
        showDisLikePopup();
    };

    const isViewerFeedbackReadOnly = isViewer;
    const showLikeFeedbackButton = isViewerFeedbackReadOnly ? likeSubmittedForAppType : !disLikeSubmittedForAppType;
    const showDisLikeFeedbackButton = isViewerFeedbackReadOnly ? disLikeSubmittedForAppType : !likeSubmittedForAppType;

    const updateAnswerState = (category: number, feedbackType: string, data: any, appType: string | undefined) => {
        const currentAnswer = selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer;
        const newAnswer = { ...currentAnswer };
        newAnswer.context.feedbacktype = feedbackType;
        newAnswer.context.feedbackgiven = true;
        newAnswer.context.feedback = feedBackInput;
        newAnswer.context.feedback.category = category;
        newAnswer.context.feedback.data = data;
        updateStreamedAnswer(primaryId as number, newAnswer, appType as AppType);
    };

    // Share functionality
    const handleShareChat = async (href: string) => {
        try {
            const queryParams = parseQueryParams(location.search);
            const sessionValue = queryParams.SessionId;
            const url = new URL(href);
            url.searchParams.set("utm_source", "aiworks");
            url.searchParams.set("utm_medium", "share_link");
            url.searchParams.set("utm_campaign", "chat_share");
            navigator.clipboard.writeText(url.toString());
            notify(i18next.t("answer.chatLinkCopied"));
            hideShareChatPopup();
            await saveSharedChatValueApi(sessionValue, "");
        } catch (error) {
            console.error("Error sharing chat:", error);
        }
    };

    // Like Component (from Answer.tsx and AnswersHistory.tsx)
    const LikeComponent: React.FC<LikeComponentProps> = ({ isLikePopupVisible, appType, isHistory, feedbackText, hideLikePopup }) => {
        const [TextFieldLikeValue, setTextFieldLikeValue] = useState("");

        const handleLikeShowHide = () => {
            setTextFieldLikeValue("");
            hideLikePopup();
        };

        // For history view, show read-only feedback
        if (isHistory) {
            return isLikePopupVisible ? (
                <Layer>
                    <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={hideLikePopup}>
                        <Overlay onClick={hideLikePopup} />
                        <FocusTrapZone>
                            <div role="document" className={popupStyles.content}>
                                <div>
                                    <IconButton style={{ color: "green" }} iconProps={{ iconName: "LikeSolid" }} />
                                    <span style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>{i18next.t("answer.chatFeedback")}</span>
                                    <IconButton style={{ color: "green", float: "right" }} iconProps={{ iconName: "Cancel" }} onClick={hideLikePopup} />
                                </div>
                                <div>
                                    <TextField multiline resizable={false} value={feedbackText} />
                                </div>
                            </div>
                        </FocusTrapZone>
                    </Popup>
                </Layer>
            ) : null;
        }

        // For active view, allow feedback submission
        return isLikePopupVisible ? (
            <Layer>
                <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={handleLikeShowHide}>
                    <FocusTrapZone>
                        <div role="document" className={popupStyles.content}>
                            <div>
                                <IconButton style={{ color: "green" }} iconProps={{ iconName: "LikeSolid" }} />
                                <span style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>{i18next.t("answer.additionalFeedback")}</span>
                                <IconButton style={{ color: "black", float: "right" }} iconProps={{ iconName: "Cancel" }} onClick={handleLikeShowHide} />
                            </div>
                            <Separator></Separator>
                            <div>
                                <TextField
                                    autoFocus
                                    className={styles.thumbTextBox}
                                    multiline
                                    resizable={false}
                                    placeholder={i18next.t("answer.goodFeedbackText")}
                                    onChange={(e: any) => {
                                        setTextFieldLikeValue(e.target.value);
                                    }}
                                    value={TextFieldLikeValue}
                                />
                            </div>

                            <DefaultButton
                                style={{ borderRadius: "10px", marginLeft: "40%", padding: "5px" }}
                                onClick={() => {
                                    hideLikePopup();
                                    // Update feedback states and call feedback handler
                                    if (onFeedbackSubmitClicked) {
                                        updateAnswerState(1, "Positive", { text: TextFieldLikeValue }, appType);
                                        onFeedbackSubmitClicked(
                                            {
                                                text: TextFieldLikeValue,
                                                type: "up-vote",
                                                options: []
                                            } as any,
                                            appType === "ALL" ? conv_id_all : appType === "FAQ" ? conv_id_faq : conv_id_manuals,

                                            appType as AppType
                                        );
                                        // mark submitted
                                        setLikeSubmittedForAppType(true);
                                    }
                                    notify(i18next.t("master.saveSuccess"));
                                }}
                            >
                                {i18next.t("answer.submitButton")}
                            </DefaultButton>
                        </div>
                    </FocusTrapZone>
                </Popup>
            </Layer>
        ) : null;
    };

    // DisLike Component (from Answer.tsx and AnswersHistory.tsx)
    const DisLikeComponent: React.FC<DisLikeComponentProps> = ({
        isDisLikePopupVisible,
        appType,
        isHistory,
        feedbackText,
        hideDisLikePopup,
        selectedOptions
    }) => {
        const [isCheckBoxOneChecked, setIsCheckBoxOneChecked] = useState(false);
        const [isCheckBoxTwoChecked, setIsCheckBoxTwoChecked] = useState(false);
        const [isCheckBoxThreeChecked, setIsCheckBoxThreeChecked] = useState(false);
        const [TextFieldDisLikeValue, setTextFieldDisLikeValue] = useState("");
        const [fieldDownVoteValue, setfieldDownVoteValue] = useState<number[]>([]);

        const onChangeTextFieldValue = useCallback((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            setTextFieldDisLikeValue(newValue || "");
        }, []);

        const handleDisLikeShowHide = () => {
            setTextFieldDisLikeValue("");
            setIsCheckBoxOneChecked(false);
            setIsCheckBoxTwoChecked(false);
            setIsCheckBoxThreeChecked(false);
            setfieldDownVoteValue([]);
            hideDisLikePopup();
        };

        const updateCheckedValue = (isChecked: boolean | undefined, checkedValue: number) => {
            setfieldDownVoteValue(prev => {
                if (isChecked) {
                    return [...prev, checkedValue];
                } else {
                    return prev.filter(value => value !== checkedValue);
                }
            });
        };

        const fnDisableSubmitButton = () => {
            if ((isCheckBoxOneChecked || isCheckBoxTwoChecked || isCheckBoxThreeChecked) && TextFieldDisLikeValue.trim().length > 0) return false;
            else return true;
        };

        // For history view, show read-only feedback
        if (isHistory) {
            return isDisLikePopupVisible ? (
                <Layer>
                    <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={hideDisLikePopup}>
                        <Overlay onClick={hideDisLikePopup} />
                        <FocusTrapZone>
                            <div role="document" className={popupStyles.content}>
                                <div>
                                    <IconButton style={{ color: "red" }} iconProps={{ iconName: "DisLikeSolid" }} />
                                    <span style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>{i18next.t("answer.chatFeedback")}</span>
                                    <IconButton style={{ color: "green", float: "right" }} iconProps={{ iconName: "Cancel" }} onClick={hideDisLikePopup} />
                                </div>
                                <div>
                                    <TextField multiline resizable={false} autoAdjustHeight value={feedbackText} />
                                    <Stack tokens={stackTokens} style={{ padding: "5px 10px 10px 15px" }}>
                                        <Checkbox label={i18next.t("answer.notTrue")} checked={selectedOptions?.includes(1)} disabled={true} />
                                        <Checkbox label={i18next.t("answer.notHelpful")} checked={selectedOptions?.includes(2)} disabled={true} />
                                        <Checkbox label={i18next.t("answer.harmful")} checked={selectedOptions?.includes(3)} disabled={true} />
                                    </Stack>
                                </div>
                            </div>
                        </FocusTrapZone>
                    </Popup>
                </Layer>
            ) : null;
        }

        // For active view, allow feedback submission
        return isDisLikePopupVisible ? (
            <Layer>
                <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={handleDisLikeShowHide}>
                    <FocusTrapZone>
                        <div role="document" className={popupStyles.content}>
                            <div>
                                <IconButton style={{ color: "red" }} iconProps={{ iconName: "DisLikeSolid" }} />
                                <span style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>{i18next.t("answer.additionalFeedback")}</span>
                                <IconButton style={{ color: "black", float: "right" }} iconProps={{ iconName: "Cancel" }} onClick={handleDisLikeShowHide} />
                            </div>
                            <Separator></Separator>
                            <div>
                                <TextField
                                    autoFocus
                                    className={styles.thumbTextBox}
                                    multiline
                                    resizable={false}
                                    placeholder={i18next.t("answer.badFeedbackText")}
                                    onChange={onChangeTextFieldValue}
                                    value={TextFieldDisLikeValue}
                                />
                                <Stack tokens={stackTokens}>
                                    <Checkbox
                                        label={i18next.t("answer.notTrue")}
                                        onChange={(_, checked) => {
                                            setIsCheckBoxOneChecked(checked || false);
                                            updateCheckedValue(checked, 1);
                                        }}
                                        checked={isCheckBoxOneChecked}
                                    />
                                    <Checkbox
                                        label={i18next.t("answer.notHelpful")}
                                        onChange={(_, checked) => {
                                            setIsCheckBoxTwoChecked(checked || false);
                                            updateCheckedValue(checked, 2);
                                        }}
                                        checked={isCheckBoxTwoChecked}
                                    />
                                    <Checkbox
                                        label={i18next.t("answer.harmful")}
                                        onChange={(_, checked) => {
                                            setIsCheckBoxThreeChecked(checked || false);
                                            updateCheckedValue(checked, 3);
                                        }}
                                        checked={isCheckBoxThreeChecked}
                                    />
                                </Stack>
                            </div>

                            <DefaultButton
                                style={{ borderRadius: "10px", marginLeft: "40%", padding: "5px" }}
                                onClick={() => {
                                    hideDisLikePopup();
                                    // Update feedback states and call feedback handler
                                    if (onFeedbackSubmitClicked) {
                                        updateAnswerState(2, "Negative", { type: fieldDownVoteValue, text: TextFieldDisLikeValue }, appType);

                                        onFeedbackSubmitClicked(
                                            {
                                                text: TextFieldDisLikeValue,
                                                type: "down-vote",
                                                options: fieldDownVoteValue
                                            } as any,
                                            appType === "ALL" ? conv_id_all : appType === "FAQ" ? conv_id_faq : conv_id_manuals,
                                            appType as AppType
                                        );
                                        displayLink && displayLink();
                                        setDisLikeSubmittedForAppType(true);
                                    }
                                    notify(i18next.t("master.saveSuccess"));
                                }}
                                disabled={fnDisableSubmitButton()}
                            >
                                {i18next.t("answer.submitButton")}
                            </DefaultButton>
                        </div>
                    </FocusTrapZone>
                </Popup>
            </Layer>
        ) : null;
    };

    // Share Component
    const ShareComponent: React.FC<ShareProps> = ({ isSharChatPopupVisible }) => {
        if (!isSharChatPopupVisible) {
            return null;
        }
        return (
            <Layer>
                <Popup className={popupStyles.root} role="dialog" aria-modal="true" onDismiss={hideShareChatPopup}>
                    <FocusTrapZone>
                        <div role="document" className={popupStyles.content} style={{ width: "500px" }}>
                            <div style={{ padding: "5px", fontSize: "15px", fontWeight: "bold" }}>
                                <Icon iconName="share" style={{ padding: "5px", fontSize: "20px" }} />
                                <span style={{ fontSize: "19px" }}>{i18next.t("answer.shareChat")}</span>
                                <IconButton style={{ color: "black", float: "right" }} iconProps={{ iconName: "Cancel" }} onClick={hideShareChatPopup} />
                            </div>
                            <div className={styles.thumbTextBox} style={{ display: "grid", justifyContent: "center", width: "465px" }}>
                                <label style={{ fontSize: "20px" }}>{i18next.t("answer.shareChatText")}</label>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <DefaultButton
                                    autoFocus
                                    iconProps={{ iconName: "link" }}
                                    style={{ color: "#1976d2", borderRadius: "10px", padding: "5px", fontSize: "13px" }}
                                    onClick={() => {
                                        handleShareChat(window.location.href);
                                    }}
                                    text={i18next.t("answer.copyLink")}
                                ></DefaultButton>{" "}
                            </div>
                        </div>
                    </FocusTrapZone>
                </Popup>
            </Layer>
        );
    };

    // ThoughtProcessAndSourcesContentRender Component
    const ThoughtProcessAndSourcesContentRender: React.FC<{ parsedAnswer: HtmlParsedAnswer; selectedAnswer: ChatAppResponse }> = ({
        parsedAnswer,
        selectedAnswer
    }) => {
        if (!selectedAnswer || parsedAnswer.answerHtml === "" || isCurrentAnswerGenerating) return null;
        if (isSuggestionsActive) return null;
        const hasSupportingCitations = !!parsedAnswer?.citations?.some(citation => !citation.startsWith("attachment:"));
        return (
            <>
                {(isAdminUser || isAdmin) && (
                    <IconButton
                        style={{ color: "black" }}
                        iconProps={{ iconName: "Lightbulb" }}
                        title={i18next.t("answer.thoughtProcess")}
                        ariaLabel={i18next.t("answer.thoughtProcess")}
                        onClick={() => onThoughtProcessClicked()}
                        disabled={!selectedAnswer?.context?.thoughts?.length && !selectedAnswer?.context?.has_thoughts}
                    />
                )}
                {hasSupportingCitations && (
                    <IconButton
                        style={{ color: "black" }}
                        iconProps={{ iconName: "ClipboardList" }}
                        title={i18next.t("answer.supportingContent")}
                        ariaLabel={i18next.t("answer.supportingContent")}
                        onClick={() => onSupportingContentClicked()}
                    />
                )}
            </>
        );
    };

    const ALLTabContentRender: React.FC = () => {
        return (
            <React.Fragment>
                {(isLoading && allParsedAnswer.answerHtml === "" && lastAnswerIndexRefValue === Number(primaryId)) ||
                (allParsedAnswer.answerHtml !== "" && lastAnswerIndexRefValue === Number(primaryId) && isRegenerateActive) ? (
                    renderLoadingSkeleton()
                ) : allParsedAnswer.answerHtml === "" ? (
                    <Box sx={{ width: "100%", marginTop: "5%", display: "flex", flexDirection: "row" }}>
                        <Text style={{ marginTop: "4px", color: "#1976d2" }}>{i18next.t("answer.regenerateResponseInfoText")}</Text>
                        <IconButton
                            disabled={isLoading || isStreaming}
                            iconProps={{ iconName: "Refresh" }}
                            title={i18next.t("answer.regenerate")}
                            onClick={() => onSend && onSend("ALL")}
                        />
                    </Box>
                ) : (
                    <>
                        <ALLNonTabContentRender
                            allAnswer={allAnswer}
                            onCitationClicked={onCitationClicked}
                            onFollowupQuestionClicked={onFollowupQuestionClicked}
                            onAttachmentLinkClick={onAttachmentLinkClick}
                            showFollowupQuestions={showFollowupQuestions}
                            isStreaming={isStreaming}
                            isFeedbackPage={isFeedbackPage}
                            warningMessage={
                                allAnswer?.context?.has_question_id && allParsedAnswer && allParsedAnswer.citations.length === 0 ? (
                                    <Text style={{ color: "#1976d2" }}>{i18next.t("chat.appWarningInfoALL")}</Text>
                                ) : null
                            }
                        />
                    </>
                )}
            </React.Fragment>
        );
    };

    const onRegenerateOptionSelected = (option: ReasoningEffort) => {
        const selectedConvId = selectedAppType === "ALL" ? conv_id_all : selectedAppType === "FAQ" ? conv_id_faq : conv_id_manuals;
        if (reasoningEffort === option) {
            notify(i18next.t("answer.sameReasoningEffortSelected"));
            return;
        }
        onRenerateClick?.(option, selectedConvId);
    };

    const RegenerateDropdown: React.FC<RegenerateDropdownProps> = ({ visible, target, onDismiss, reasoningEffortSelected, onRegenerateOptionSelected }) => {
        if (!visible || !target) return null;

        const reasoningIcons: Record<string, string> = {
            none: "🚫",
            minimal: "⚡",
            low: "🏃",
            medium: "⚖️",
            high: "🧠"
        };

        const efforts = chatModel ? getReasoningEffortsForModel(chatModel) : GPT5_REASONING_EFFORTS;

        const items: IContextualMenuItem[] = efforts.map(effort => ({
            key: effort,
            text: `${reasoningIcons[effort] ?? ""} ${i18next.t(`reasoningEffort.${effort}`)}`,
            data: effort,
            title: i18next.t(`reasoningEffort.${effort}Tooltip`),
            canCheck: true,
            checked: reasoningEffortSelected === effort,
            onClick: () => {
                onRegenerateOptionSelected(effort as ReasoningEffort);
                onDismiss();
            }
        }));

        return <ContextualMenu items={items} target={target} isBeakVisible={true} directionalHint={DirectionalHint.topCenter} onDismiss={onDismiss} />;
    };

    const handleVersionNavigation = (direction: string) => {
        if (currentAnswerIndex != null && totalVersions) {
            let newIndex = currentAnswerIndex;
            if (direction === "prev" && currentAnswerIndex > 0) {
                newIndex = currentAnswerIndex - 1;
            } else if (direction === "next" && currentAnswerIndex < totalVersions) {
                newIndex = currentAnswerIndex + 1;
            }
            if (newIndex !== currentAnswerIndex) {
                if (onAnswerVersionChange) {
                    onAnswerVersionChange(newIndex);
                }
            }
        }
    };

    // Render the unified component
    return (
        <>
            {/* Tour component */}
            {isLastAnswer && (
                <>
                    <Joyride
                        continuous
                        steps={tourOne.steps}
                        callback={data => {
                            if (!isStreaming) {
                                handleTourCallback(data, false);
                            }
                        }}
                        run={tourOne.run}
                        hideCloseButton
                        scrollToFirstStep
                        showSkipButton
                        disableCloseOnEsc
                        disableOverlayClose
                        spotlightPadding={1}
                        locale={{
                            next: i18next.t(`appTour.next`),
                            skip: i18next.t(`appTour.skip`),
                            back: i18next.t(`appTour.back`),
                            last: i18next.t(`appTour.last`)
                        }}
                    />
                    <Joyride
                        continuous
                        steps={tourTwo.steps}
                        callback={data => {
                            if (!isStreaming) {
                                handleTourCallback(data, true);
                            }
                        }}
                        run={tourTwo.run}
                        hideCloseButton
                        scrollToFirstStep
                        showSkipButton
                        disableCloseOnEsc
                        disableOverlayClose
                        spotlightPadding={1}
                        locale={{
                            next: i18next.t(`appTour.next`),
                            skip: i18next.t(`appTour.skip`),
                            back: i18next.t(`appTour.back`),
                            last: i18next.t(`appTour.last`)
                        }}
                    />
                </>
            )}

            <Stack>
                <Stack
                    id={String(primaryId)}
                    className={`${styles.answerContainer} ${isSelected && styles.selected}`}
                    style={{
                        ...(doctype && { backgroundColor: "rgb(240, 255, 240)" })
                    }}
                    verticalAlign="space-between"
                >
                    <Stack.Item>
                        <div>
                            <TabContext value={tabIndex}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "8px",
                                        gap: "12px"
                                    }}
                                >
                                    {/* Left section: AnswerIcon + optional LockIcon */}
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <AnswerIcon
                                            language={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.language
                                            }
                                            documenttype={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.document_category
                                            }
                                            chatModel={chatModel}
                                            reasoningEffort={reasoningEffort}
                                            product={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.product_filter
                                            }
                                            productVersion={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.productVersion
                                            }
                                            sub_product={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.sub_product_filter
                                            }
                                            feedbackCorpId={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.feedback_corp_id
                                            }
                                            feedbackUser={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context
                                                    ?.feedback_user
                                            }
                                            CorpId={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.corpId
                                            }
                                            UserId={
                                                (selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer)?.context?.userId
                                            }
                                            isFeedbackPage={isFeedbackPage}
                                            apptype={selectedAppType}
                                            conversationDate={currentConversationDate}
                                            customerConfigVersionMajor={
                                                isFeedbackPage && (isAdminUser || isAdmin) ? autoSelectionSnapshot?.customerConfigVersionMajor : null
                                            }
                                        />
                                        {doctype && <LockIcon />}
                                    </div>

                                    <div className={`answer-content-${primaryId}`} style={{ flex: 1 }}>
                                        {showAllOnly ? (
                                            // no tabs — just drop a marker for tour id (optional)
                                            <span data-tour-id={`ALLTab${primaryId}`} />
                                        ) : (
                                            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                                <TabList onChange={handleChange} aria-label="lab API tabs example" className={`answer-tab-${primaryId}`}>
                                                    {showAllTab && (
                                                        <Tab data-tour-id={`ALLTab${primaryId}`} label={i18next.t("chat.ALL")} value={"ALL" + primaryId} />
                                                    )}
                                                    <Tab data-tour-id={`FAQTab${primaryId}`} label={i18next.t("chat.faq")} value={"FAQ" + primaryId} />
                                                    <Tab
                                                        data-tour-id={`ManualsTab${primaryId}`}
                                                        label={i18next.t("chat.manuals")}
                                                        value={"Manuals" + primaryId}
                                                    />
                                                </TabList>
                                            </Box>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                        <ThoughtProcessAndSourcesContentRender
                                            parsedAnswer={
                                                selectedAppType === "ALL" ? allParsedAnswer : selectedAppType === "FAQ" ? faqParsedAnswer : manualParsedAnswer
                                            }
                                            selectedAnswer={selectedAppType === "ALL" ? allAnswer : selectedAppType === "FAQ" ? answer : manualsAnswer}
                                        />
                                    </div>
                                </div>
                                <ReasoningSummary reasoningSummary={isCurrentAnswerGenerating ? null : currentReasoningSummary} />
                                {showAllOnly ? (
                                    <ALLTabContentRender />
                                ) : (
                                    <>
                                        {/* ALL Tab panels */}
                                        {showAllTab && (
                                            <TabPanel value={"ALL" + primaryId} sx={{ padding: 0, width: "100%" }}>
                                                <ALLTabContentRender />
                                            </TabPanel>
                                        )}

                                        {/* FAQ Tab Panel */}
                                        <TabPanel value={"FAQ" + primaryId} sx={{ padding: 0, width: "100%" }}>
                                            {!String(window.location.pathname).includes("/assistant/feedbacks") &&
                                            selectedProduct?.faq_key === "" &&
                                            faqParsedAnswer.answerHtml === "" ? (
                                                <Text style={{ color: "#1976d2" }}>{i18next.t("answer.documentUnavailable")}</Text>
                                            ) : (isLoading && faqParsedAnswer.answerHtml === "" && lastAnswerIndexRefValue === Number(primaryId)) ||
                                              (faqParsedAnswer.answerHtml !== "" && lastAnswerIndexRefValue === Number(primaryId) && isRegenerateActive) ? (
                                                renderLoadingSkeleton()
                                            ) : faqParsedAnswer.answerHtml === "" ? (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        marginTop: "5%",
                                                        display: "flex",
                                                        flexDirection: "row"
                                                    }}
                                                >
                                                    <Text style={{ marginTop: "4px", color: "#1976d2" }}>{i18next.t("answer.regenerateResponseInfoText")}</Text>
                                                    {selectedProduct?.faq_key !== "" && (
                                                        <IconButton
                                                            disabled={isLoading || isStreaming}
                                                            iconProps={{ iconName: "Refresh" }}
                                                            title={i18next.t("answer.regenerate")}
                                                            onClick={() => onSend && onSend("FAQ")}
                                                        />
                                                    )}
                                                </Box>
                                            ) : (
                                                <>
                                                    <div>
                                                        <AnswerWithPreCodeDisplay
                                                            sanitizedHTML={faqParsedAnswer.answerHtml}
                                                            onAttachmentLinkClick={(name, page) => onAttachmentLinkClick && onAttachmentLinkClick(name, page)}
                                                        />
                                                    </div>
                                                    <CitationList
                                                        appType="FAQ"
                                                        citations={faqParsedAnswer.citations}
                                                        dataPoints={faqDataPoints}
                                                        onCitationClicked={onCitationClicked}
                                                        onAttachmentLinkClick={onAttachmentLinkClick}
                                                    />
                                                    {answer?.context?.has_question_id && faqParsedAnswer && faqParsedAnswer.citations.length === 0 && (
                                                        <Text style={{ color: "#1976d2" }}>{i18next.t("chat.appWarningInfoFAQ")}</Text>
                                                    )}
                                                    {!!faqFollowupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                                                        <Stack.Item>
                                                            <Stack
                                                                horizontal
                                                                wrap
                                                                className={`${faqParsedAnswer.citations.length ? styles.followupQuestionsList : ""}`}
                                                                tokens={{ childrenGap: 6 }}
                                                            >
                                                                <span className={styles.followupQuestionLearnMore}>
                                                                    {i18next.t("answer.followupQuestions")}:
                                                                </span>
                                                                {faqFollowupQuestions.map((x, i) => {
                                                                    return (
                                                                        <a
                                                                            key={i}
                                                                            className={styles.followupQuestion}
                                                                            title={x}
                                                                            onClick={() => onFollowupQuestionClicked(x, "FAQ")}
                                                                        >
                                                                            {`${x}`}
                                                                        </a>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Stack.Item>
                                                    )}
                                                </>
                                            )}
                                        </TabPanel>

                                        {/* Manuals Tab Panel */}
                                        <TabPanel value={"Manuals" + primaryId} sx={{ padding: 0, width: "100%" }}>
                                            {!String(window.location.pathname).includes("/assistant/feedbacks") &&
                                            selectedProduct?.manual_key === "" &&
                                            manualParsedAnswer.answerHtml === "" ? (
                                                <Text style={{ color: "#1976d2" }}>{i18next.t("answer.documentUnavailable")}</Text>
                                            ) : (isLoading && manualParsedAnswer.answerHtml === "" && lastAnswerIndexRefValue === Number(primaryId)) ||
                                              (manualParsedAnswer.answerHtml !== "" && lastAnswerIndexRefValue === Number(primaryId) && isRegenerateActive) ? (
                                                renderLoadingSkeleton()
                                            ) : manualParsedAnswer.answerHtml === "" ? (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        marginTop: "5%",
                                                        display: "flex",
                                                        flexDirection: "row"
                                                    }}
                                                >
                                                    <Text style={{ marginTop: "4px", color: "#1976d2" }}>{i18next.t("answer.regenerateResponseInfoText")}</Text>
                                                    {selectedProduct?.manual_key !== "" && (
                                                        <IconButton
                                                            disabled={isLoading || isStreaming}
                                                            iconProps={{ iconName: "Refresh" }}
                                                            title={i18next.t("answer.regenerate")}
                                                            onClick={() => onSend && onSend("Manuals")}
                                                        />
                                                    )}
                                                </Box>
                                            ) : (
                                                <>
                                                    <div>
                                                        <AnswerWithPreCodeDisplay
                                                            sanitizedHTML={manualParsedAnswer.answerHtml}
                                                            onAttachmentLinkClick={(name, page) => onAttachmentLinkClick && onAttachmentLinkClick(name, page)}
                                                        />
                                                    </div>
                                                    <CitationList
                                                        appType="Manuals"
                                                        citations={manualParsedAnswer.citations}
                                                        dataPoints={manualsDataPoints}
                                                        onCitationClicked={onCitationClicked}
                                                        onAttachmentLinkClick={onAttachmentLinkClick}
                                                    />
                                                    {manualsAnswer?.context?.has_question_id &&
                                                        manualParsedAnswer &&
                                                        manualParsedAnswer.citations.length === 0 && (
                                                            <Text style={{ color: "#1976d2" }}>{i18next.t("chat.appWarningInfoManual")}</Text>
                                                        )}
                                                    {!!manualsFollowupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                                                        <Stack.Item>
                                                            <Stack
                                                                horizontal
                                                                wrap
                                                                className={`${manualParsedAnswer.citations.length ? styles.followupQuestionsList : ""}`}
                                                                tokens={{ childrenGap: 6 }}
                                                            >
                                                                <span className={styles.followupQuestionLearnMore}>
                                                                    {i18next.t("answer.followupQuestions")}:
                                                                </span>
                                                                {manualsFollowupQuestions.map((x, i) => {
                                                                    return (
                                                                        <a
                                                                            key={i}
                                                                            className={styles.followupQuestion}
                                                                            title={x}
                                                                            onClick={() => onFollowupQuestionClicked(x, "Manuals")}
                                                                        >
                                                                            {`${x}`}
                                                                        </a>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Stack.Item>
                                                    )}
                                                </>
                                            )}
                                        </TabPanel>
                                    </>
                                )}
                            </TabContext>
                        </div>
                    </Stack.Item>
                    <Stack.Item>
                        {/* Row 1: "Generated for" indicator — above the action row, hidden while generating */}
                        {showGeneratedForProductContext && !isCurrentAnswerGenerating && (
                            <div className={styles.generatedForProductIndicator}>
                                <span className={styles.generatedForProductLabel}>
                                    {i18next.t("answer.generatedForProductLabel", { defaultValue: "Generated for" })}
                                </span>
                                <span className={styles.generatedForProductValue}>{generatedForProductText}</span>
                                {autoSelectionSnapshot && (
                                    <>
                                        <span className={styles.generatedForAutoBadge}>{i18next.t("answer.autoSelectedBadge")}</span>
                                        <button
                                            type="button"
                                            className={styles.generatedForWhyToggle}
                                            onClick={() => setShowAutoSelectionSnapshot(previous => !previous)}
                                        >
                                            {showAutoSelectionSnapshot
                                                ? i18next.t("answer.autoSelectionSnapshotHideLabel")
                                                : i18next.t("answer.autoSelectionSnapshotShowLabel")}
                                        </button>
                                    </>
                                )}
                                {onChangeProductScenarioAndRegenerate && !isSuggestionsActive && (
                                    <Tooltip
                                        title={i18next.t("answer.changeProductScenarioAndRegenerate", {
                                            defaultValue: "Change product scenario and regenerate answer"
                                        })}
                                    >
                                        <span>
                                            <MuiIconButton
                                                id={`ChangeProduct${primaryId}`}
                                                size="small"
                                                color="primary"
                                                onClick={onChangeProductScenarioAndRegenerate}
                                            >
                                                <SwapHorizIcon fontSize="small" />
                                            </MuiIconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        {showGeneratedForProductContext && !isCurrentAnswerGenerating && autoSelectionSnapshot && showAutoSelectionSnapshot && (
                            <div className={styles.autoSelectionSnapshotCard}>
                                <div className={styles.autoSelectionSnapshotTitle}>{i18next.t("answer.autoSelectionSnapshotTitle")}</div>
                                <div className={styles.autoSelectionSnapshotGrid}>
                                    <div className={styles.autoSelectionSnapshotRow}>
                                        <span className={styles.autoSelectionSnapshotKey}>{i18next.t("answer.autoSelectionSnapshotSelectedProductLabel")}</span>
                                        <span className={styles.autoSelectionSnapshotValue}>{autoSelectionSnapshot.selectedProductLabel}</span>
                                    </div>
                                    {/* Admin-only, feedback page only: expose the PTF customer-config override audit trail.
                                        Only meaningful for the backend/server-model decision — the frontend heuristic
                                        fallback never resolves PTF entitlement, so these fields would just be empty. */}
                                    {isFeedbackPage && (isAdminUser || isAdmin) && isServerModelSelection && (
                                        <>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>
                                                    {i18next.t("answer.autoSelectionSnapshotModelSelectedProductLabel")}
                                                </span>
                                                <span className={styles.autoSelectionSnapshotValue}>
                                                    {autoSelectionSnapshot.modelSelectedProductLabel ?? autoSelectionSnapshot.selectedProductLabel}
                                                </span>
                                            </div>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>
                                                    {i18next.t("answer.autoSelectionSnapshotModelSelectedScoreLabel")}
                                                </span>
                                                <span className={styles.autoSelectionSnapshotValue}>{modelSelectedScoreText}</span>
                                            </div>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>
                                                    {i18next.t("answer.autoSelectionSnapshotPtfSelectedScoreLabel")}
                                                </span>
                                                <span className={styles.autoSelectionSnapshotValue}>{ptfSelectedScoreText}</span>
                                            </div>
                                            {autoSelectionSnapshot.customerConfigRegistered !== null &&
                                                autoSelectionSnapshot.customerConfigRegistered !== undefined && (
                                                    <div className={styles.autoSelectionSnapshotRow}>
                                                        <span className={styles.autoSelectionSnapshotKey}>
                                                            {i18next.t("answer.autoSelectionSnapshotConfigRegisteredLabel")}
                                                        </span>
                                                        <span className={styles.autoSelectionSnapshotValue}>
                                                            {autoSelectionSnapshot.customerConfigRegistered
                                                                ? i18next.t("answer.autoSelectionSnapshotConfigAppliedYes")
                                                                : i18next.t("answer.autoSelectionSnapshotConfigAppliedNo")}
                                                        </span>
                                                    </div>
                                                )}
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>
                                                    {i18next.t("answer.autoSelectionSnapshotConfigAppliedLabel")}
                                                </span>
                                                <span className={styles.autoSelectionSnapshotValue}>
                                                    {autoSelectionSnapshot.customerConfigApplied
                                                        ? i18next.t("answer.autoSelectionSnapshotConfigAppliedYes")
                                                        : i18next.t("answer.autoSelectionSnapshotConfigAppliedNo")}
                                                </span>
                                            </div>
                                            {customerConfigComparisonText && (
                                                <div className={styles.autoSelectionSnapshotRow}>
                                                    <span className={styles.autoSelectionSnapshotKey}>
                                                        {i18next.t("answer.autoSelectionSnapshotConfigComparisonLabel")}
                                                    </span>
                                                    <span className={styles.autoSelectionSnapshotValue}>{customerConfigComparisonText}</span>
                                                </div>
                                            )}
                                            {customerConfigStatusText && (
                                                <div className={styles.autoSelectionSnapshotRow}>
                                                    <span className={styles.autoSelectionSnapshotKey}>
                                                        {i18next.t("answer.autoSelectionSnapshotConfigStatusLabel")}
                                                    </span>
                                                    <span className={styles.autoSelectionSnapshotValue}>{customerConfigStatusText}</span>
                                                </div>
                                            )}
                                            {Array.isArray(autoSelectionSnapshot.precheckCandidates) && autoSelectionSnapshot.precheckCandidates.length > 0 && (
                                                <div className={styles.autoSelectionSnapshotRow}>
                                                    <span className={styles.autoSelectionSnapshotKey}>
                                                        {i18next.t("answer.autoSelectionSnapshotCandidatesLabel")}
                                                    </span>
                                                    <span className={styles.autoSelectionSnapshotValue}>
                                                        {autoSelectionSnapshot.precheckCandidates.map((candidate, index) => (
                                                            <div key={`${candidate.display_label}-${index}`}>
                                                                {`${index + 1}. ${candidate.display_label} - ${formatScoreAndCitations(
                                                                    candidate.average_score,
                                                                    candidate.citation_count
                                                                )}`}
                                                            </div>
                                                        ))}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className={styles.autoSelectionSnapshotRow}>
                                        <span className={styles.autoSelectionSnapshotKey}>{i18next.t("answer.autoSelectionSnapshotSelectionMethodLabel")}</span>
                                        <span className={styles.autoSelectionSnapshotValue}>{autoSelectionMethodText}</span>
                                    </div>
                                    {autoSelectionDecisionSourceText && (
                                        <div className={styles.autoSelectionSnapshotRow}>
                                            <span className={styles.autoSelectionSnapshotKey}>
                                                {i18next.t("answer.autoSelectionSnapshotDecisionSourceLabel")}
                                            </span>
                                            <span className={styles.autoSelectionSnapshotValue}>{autoSelectionDecisionSourceText}</span>
                                        </div>
                                    )}
                                    {autoSelectionDecisionReason && (
                                        <div className={styles.autoSelectionSnapshotRow}>
                                            <span className={styles.autoSelectionSnapshotKey}>
                                                {i18next.t("answer.autoSelectionSnapshotDecisionReasonLabel")}
                                            </span>
                                            <span className={styles.autoSelectionSnapshotValue}>{autoSelectionDecisionReason}</span>
                                        </div>
                                    )}
                                    <div className={styles.autoSelectionSnapshotRow}>
                                        <span className={styles.autoSelectionSnapshotKey}>{autoSelectionPrimaryMetricLabel}</span>
                                        <span className={styles.autoSelectionSnapshotValue}>{autoSelectionPrimaryMetricValue}</span>
                                    </div>
                                    {!isServerModelSelection && (
                                        <>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>
                                                    {i18next.t("answer.autoSelectionSnapshotSecondProductLabel")}
                                                </span>
                                                <span className={styles.autoSelectionSnapshotValue}>{autoSelectionSecondProductText}</span>
                                            </div>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>{autoSelectionSecondMetricLabel}</span>
                                                <span className={styles.autoSelectionSnapshotValue}>{autoSelectionSecondMetricValue}</span>
                                            </div>
                                            <div className={styles.autoSelectionSnapshotRow}>
                                                <span className={styles.autoSelectionSnapshotKey}>{autoSelectionLeadLabel}</span>
                                                <span className={styles.autoSelectionSnapshotValue}>{autoSelectionLeadValue}</span>
                                            </div>
                                        </>
                                    )}
                                    <div className={styles.autoSelectionSnapshotRow}>
                                        <span className={styles.autoSelectionSnapshotKey}>{i18next.t("answer.autoSelectionSnapshotTotalCitationsLabel")}</span>
                                        <span className={styles.autoSelectionSnapshotValue}>{autoSelectionSnapshot.citationCount}</span>
                                    </div>
                                    <div className={styles.autoSelectionSnapshotRow}>
                                        <span className={styles.autoSelectionSnapshotKey}>{i18next.t("answer.autoSelectionSnapshotCandidateCountLabel")}</span>
                                        <span className={styles.autoSelectionSnapshotValue}>{autoSelectionSnapshot.candidateCount}</span>
                                    </div>
                                </div>
                                {!isServerModelSelection && (
                                    <div className={styles.autoSelectionSnapshotThresholds}>
                                        {isRerankerSelection ? (
                                            <span className={styles.autoSelectionSnapshotThreshold}>
                                                {`${i18next.t("supportingContent.rerankerScore")} >= ${Number(
                                                    autoSelectionSnapshot.minAvgRerankerScore ?? 0
                                                ).toFixed(2)}`}
                                            </span>
                                        ) : (
                                            <span className={styles.autoSelectionSnapshotThreshold}>
                                                {i18next.t("answer.autoSelectionSnapshotMinConfidenceLabel", {
                                                    value: Math.round(autoSelectionSnapshot.minConfidence)
                                                })}
                                            </span>
                                        )}
                                        <span className={styles.autoSelectionSnapshotThreshold}>
                                            {i18next.t("answer.autoSelectionSnapshotMinHitsLabel", {
                                                value: autoSelectionSnapshot.minHits
                                            })}
                                        </span>
                                        {isRerankerSelection ? (
                                            <span className={styles.autoSelectionSnapshotThreshold}>
                                                {`${i18next.t("answer.autoSelectionSnapshotLeadLabel")} >= ${Number(
                                                    autoSelectionSnapshot.minAvgRerankerGap ?? 0
                                                ).toFixed(2)}`}
                                            </span>
                                        ) : (
                                            <span className={styles.autoSelectionSnapshotThreshold}>
                                                {i18next.t("answer.autoSelectionSnapshotMinMarginLabel", {
                                                    value: Math.round(autoSelectionSnapshot.minMargin)
                                                })}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Row 2: version nav (left) + feedback icons (right) — all on one horizontal line */}
                        <Stack
                            horizontal
                            verticalAlign="center"
                            horizontalAlign="space-between"
                            styles={{
                                root: {
                                    width: "100%"
                                }
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "2px" }}>
                                {totalVersions > 1 && !isCurrentAnswerGenerating && (
                                    <MUIStack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title={i18next.t("answer.previousVersion")}>
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleVersionNavigation("prev")}
                                                    disabled={currentAnswerIndex === 0}
                                                >
                                                    <KeyboardArrowLeftIcon fontSize="small" />
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>

                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: "#666",
                                                minWidth: 30,
                                                textAlign: "center",
                                                userSelect: "none"
                                            }}
                                        >
                                            {(currentAnswerIndex ?? 0) + 1}/{totalVersions}
                                        </span>

                                        <Tooltip title={i18next.t("answer.nextVersion")}>
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleVersionNavigation("next")}
                                                    disabled={(currentAnswerIndex ?? 0) + 1 === totalVersions}
                                                >
                                                    <KeyboardArrowRightIcon fontSize="small" />
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>
                                    </MUIStack>
                                )}
                            </div>

                            {!hidePerRowFeedback && (
                                <Stack
                                    id={`feedback-content-${primaryId}`}
                                    horizontal
                                    horizontalAlign="end"
                                    styles={{
                                        root: {
                                            marginTop: "0px"
                                        }
                                    }}
                                >
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                        {showLikeFeedbackButton && (
                                            <IconButton
                                                id={`LikeIcon${primaryId}`}
                                                iconProps={{ iconName: likeSubmittedForAppType ? "LikeSolid" : "Like" }}
                                                title={i18next.t("answer.good")}
                                                onClick={isViewerFeedbackReadOnly ? undefined : handleLikeClicked}
                                            />
                                        )}
                                        {showDisLikeFeedbackButton && (
                                            <IconButton
                                                id={`DisLikeIcon${primaryId}`}
                                                iconProps={{ iconName: disLikeSubmittedForAppType ? "DislikeSolid" : "Dislike" }}
                                                title={i18next.t("answer.bad")}
                                                onClick={isViewerFeedbackReadOnly ? undefined : handleDislikeClicked}
                                            />
                                        )}
                                        {!isFeedbackPage && !!onRenerateClick && (
                                            <div
                                                ref={dropdownRef}
                                                style={{
                                                    position: "relative",
                                                    display: "inline-flex",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <IconButton
                                                    id={`Regenerate${primaryId}`}
                                                    iconProps={{ iconName: "sync" }}
                                                    title={i18next.t("answer.regenerate")}
                                                    disabled={isSuggestionsActive}
                                                    onClick={ev => {
                                                        const supportsReasoningChange = getReasoningEffortsForModel(chatModel ?? "").length > 0;
                                                        if (supportsReasoningChange) {
                                                            setRegenerateTarget(ev.currentTarget as HTMLElement);
                                                            setShowRegenerateMenu(prev => !prev);
                                                        } else {
                                                            const convId =
                                                                selectedAppType === "ALL"
                                                                    ? conv_id_all
                                                                    : selectedAppType === "FAQ"
                                                                      ? conv_id_faq
                                                                      : conv_id_manuals;
                                                            onRenerateClick(reasoningEffort as ReasoningEffort, convId);
                                                        }
                                                    }}
                                                />

                                                {showRegenerateMenu && (
                                                    <RegenerateDropdown
                                                        visible={showRegenerateMenu}
                                                        target={regenerateTarget}
                                                        reasoningEffortSelected={reasoningEffort}
                                                        onDismiss={() => setShowRegenerateMenu(false)}
                                                        onRegenerateOptionSelected={effort => {
                                                            onRegenerateOptionSelected(effort);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {((isFeedbackPage && !isViewer) || !isFeedbackPage) && (
                                            <IconButton
                                                id={`ShareIcon${primaryId}`}
                                                iconProps={{ iconName: "Share" }}
                                                title={i18next.t("chat.shareChat")}
                                                onClick={showShareChatPopup}
                                            />
                                        )}
                                    </div>
                                </Stack>
                            )}
                        </Stack>
                    </Stack.Item>
                </Stack>
                {isLastAnswer && (
                    <Text className={styles.lastInfo} style={{ marginTop: "12px" }}>
                        {i18next.t("chat.responseInfo")}
                    </Text>
                )}
            </Stack>

            {/* Popup components */}
            <LikeComponent
                isLikePopupVisible={isLikePopupVisible}
                appType={selectedAppType}
                isHistory={isHistory}
                feedbackText={feedBackInput["data"] && feedBackInput["data"]["text"]}
                hideLikePopup={hideLikePopup}
            />
            <DisLikeComponent
                isDisLikePopupVisible={isDisLikePopupVisible}
                appType={selectedAppType}
                isHistory={isHistory}
                feedbackText={feedBackInput["data"] && feedBackInput["data"]["text"]}
                hideDisLikePopup={hideDisLikePopup}
                selectedOptions={feedBackInput["data"] && feedBackInput["data"]["type"]}
            />
            <ShareComponent isSharChatPopupVisible={isSharChatPopupVisible} />
            <Toaster toasterId={UNIFIED_TOASTER_ID} position="bottom-start" pauseOnHover pauseOnWindowBlur timeout={2000} />
            {/* Product unavailable popup */}
            <SelectedProductUnAvailableInfoMsgDialog
                showWarningFlag={isSelectedProductUnAvailablePopupOpen}
                warningMsg={productUnAvailableWarningMsg}
                onClose={() => {
                    setIsSelectedProductUnAvailablePopupOpen(false);
                    setProductUnAvailableWarningMsg("");
                }}
            />
        </>
    );
};
